'use client';

import type { UserBike } from 'models/shared';
import { useMemo, useState } from 'react';
import { MotionDiv } from 'motion';
import { CheckmarkIcon, PlusIcon } from '@c/icons';
import type { CategoryPageSideFilters } from '@c/CategoryOrSubcategoryPage';
import { useAuth } from 'context/AuthContext';
import SafeImage from './SafeImage';
import { ShopByVehicle } from './ShopByVehicle';
import type { PageSideFilters } from '@util/hooks/useProductsPage';
import { prefix } from '@util/imagePrefix';
import { cn } from '@util/index';
import MobileSearchByVehicle from 'app/product/[slug]/components/SearchByVehicle/MobileSearchByVehicle';
import MobileEditGarageModal from '@c/filters/components/MySizesGarage/components/MobileEditGarageModal';
import type { Bike } from 'models/shared';
import { updateDoc, doc } from 'firebase/firestore';
import { db } from '@util/firebase';
import { useToastContext } from 'context/ToastContext';
import { logError } from '@util/logError';

interface ShopYourGarageProps {
  onBikeComplete: (bike: UserBike | null) => void;
  onToggleBikeFilter?: (enabled: boolean) => void;
  sideFilters: CategoryPageSideFilters;
  handleFilterRemove: (filter: { key: string; value: any }) => void;
  showMobileEditGarageModal: boolean;
  setShowMobileEditGarageModal: (show: boolean) => void;
  prompt?: { title: string; description: string };
}

export default function ShopYourGarage({
  onBikeComplete,
  onToggleBikeFilter,
  sideFilters,
  showMobileEditGarageModal,
  setShowMobileEditGarageModal,
  prompt,
}: ShopYourGarageProps) {
  const { userDoc } = useAuth();
  const [showMobileSearchByVehicle, setShowMobileSearchByVehicle] =
    useState(false);
  const [searchByVehicleVariant, setSearchByVehicleVariant] = useState<
    'add-to-garage' | 'filter'
  >('filter');
  const { showSuccessToast, showErrorToast } = useToastContext();

  const selectedBike = {
    year: sideFilters.bikeYears?.[0]
      ? parseInt(sideFilters.bikeYears[0] as string)
      : undefined,
    make: sideFilters.bikeMakes?.[0] || '',
    model: sideFilters.bikeModels?.[0] || '',
  };

  const isBikeMatching = (bike: UserBike) => {
    return (
      selectedBike.make === bike.make &&
      selectedBike.model === bike.model &&
      selectedBike.year === bike.year
    );
  };

  const handleBikeComplete = (bike: UserBike | null) => {
    if (bike && !isBikeMatching(bike)) {
      onBikeComplete(bike);
    } else {
      onBikeComplete(null);
    }
  };

  const handleSyncSelectedBikes = async (bikes: Bike[]) => {
    if (!userDoc) return;

    try {
      const userRef = doc(db, 'users', userDoc.uid);
      await updateDoc(userRef, { bikes });
      showSuccessToast('Garage updated successfully');
      setShowMobileEditGarageModal(false);
    } catch (error) {
      console.error('Error updating garage:', error);
      showErrorToast('Failed to update garage');
    }
  };

  const handleAddBikeToGarage = async (bike: UserBike) => {
    if (!userDoc) return;

    const bikeExists = userDoc.bikes?.some(
      (b) =>
        b.make === bike.make && b.model === bike.model && b.year === bike.year
    );

    if (bikeExists) {
      showErrorToast('Vehicle already in your garage');
      setShowMobileSearchByVehicle(false);
      return;
    }

    try {
      const updatedBikes = [...(userDoc.bikes || []), bike];
      const userRef = doc(db, 'users', userDoc.uid);
      await updateDoc(userRef, { bikes: updatedBikes });
      onBikeComplete(bike);

      setShowMobileSearchByVehicle(false);
    } catch (error) {
      logError(error, 'Error adding bike to garage');
    }
  };

  const handleAddBikeClick = () => {
    setShowMobileEditGarageModal(false);
    setSearchByVehicleVariant('add-to-garage');
    setShowMobileSearchByVehicle(true);
  };

  const sortedBikes = useMemo(() => {
    const bikes = userDoc?.bikes || [];

    const currentSelectedBike = {
      year: sideFilters.bikeYears?.[0]
        ? parseInt(sideFilters.bikeYears[0] as string)
        : undefined,
      make: sideFilters.bikeMakes?.[0] || '',
      model: sideFilters.bikeModels?.[0] || '',
    };

    const isBikeMatchingCurrent = (bike: UserBike) => {
      return (
        currentSelectedBike.make === bike.make &&
        currentSelectedBike.model === bike.model &&
        currentSelectedBike.year === bike.year
      );
    };

    const sortBikes = (bikesToSort: UserBike[]) => {
      return bikesToSort.sort((a, b) => {
        const isASelected = isBikeMatchingCurrent(a);
        const isBSelected = isBikeMatchingCurrent(b);

        if (isASelected && !isBSelected) return -1;
        if (!isASelected && isBSelected) return 1;

        const dateA = a.date_added ? new Date(a.date_added).getTime() : 0;
        const dateB = b.date_added ? new Date(b.date_added).getTime() : 0;
        return dateB - dateA;
      });
    };

    const selectedBikeExists =
      currentSelectedBike.make &&
      currentSelectedBike.model &&
      currentSelectedBike.year;

    const selectedBikeIsNotInList =
      selectedBikeExists &&
      !bikes.some(
        (b) =>
          b.make === currentSelectedBike.make &&
          b.model === currentSelectedBike.model &&
          b.year === currentSelectedBike.year
      );

    if (selectedBikeIsNotInList) {
      return sortBikes([currentSelectedBike as UserBike, ...bikes]);
    }

    return sortBikes(bikes);
  }, [
    userDoc?.bikes,
    sideFilters.bikeYears,
    sideFilters.bikeMakes,
    sideFilters.bikeModels,
  ]);

  const renderBikeCards = (className: string) => {
    return (
      <div className={className}>
        <div className="flex h-[10rem] flex-nowrap gap-x-8 pb-4">
          <button
            type="button"
            className="flex h-full w-fit items-center justify-center rounded-[2rem] bg-white px-12 py-6 shadow-md shadow-black/10"
            onClick={() => {
              setSearchByVehicleVariant('add-to-garage');
              setShowMobileSearchByVehicle(true);
            }}
          >
            <div className="flex flex-col items-center gap-2 text-[1.8rem] font-semibold text-brand-secondary">
              <PlusIcon className="h-12 w-12" />
              Add
            </div>
          </button>

          {sortedBikes.map((bike, i) => (
            <BikeCard
              key={`${bike.year} ${bike.make} ${bike.model} ${i}`}
              bike={bike}
              onBikeSelect={handleBikeComplete}
              isSelected={isBikeMatching(bike)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="w-full bg-[#F6F6F6] px-[1.8rem] pb-6 pt-2">
        <div className="mx-auto max-w-content">
          <div className="flex w-full flex-col gap-[0.2rem]  sm:bg-white">
            {/* Shop By Vehicle - Mobile Only */}
            <div className="-mx-[1.8rem] sm:hidden">
              <ShopByVehicle
                sideFilters={sideFilters as PageSideFilters}
                onBikeComplete={onBikeComplete}
                onToggleBikeFilter={onToggleBikeFilter}
                setShowMobileEditGarageModal={setShowMobileEditGarageModal}
                prompt={prompt}
              />
            </div>

            {!!userDoc?.bikes?.length && (
              <>
                {renderBikeCards(
                  'sm:scrollbar-auto flex w-full items-center gap-x-12 overflow-x-auto scrollbar-none sm:hidden'
                )}

                {renderBikeCards(
                  'hidden w-full items-center gap-x-12 overflow-x-auto sm:flex my-8 pb-2'
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <MobileSearchByVehicle
        show={showMobileSearchByVehicle}
        dismiss={() => setShowMobileSearchByVehicle(false)}
        variant={searchByVehicleVariant}
        onBikeComplete={
          searchByVehicleVariant === 'add-to-garage'
            ? handleAddBikeToGarage
            : handleBikeComplete
        }
        onClear={
          searchByVehicleVariant === 'filter' &&
          selectedBike.year &&
          selectedBike.make &&
          selectedBike.model
            ? () => onBikeComplete(null)
            : undefined
        }
        initialBike={
          searchByVehicleVariant === 'filter' &&
          selectedBike.year &&
          selectedBike.make &&
          selectedBike.model
            ? (selectedBike as Partial<UserBike>)
            : null
        }
        userDoc={userDoc}
      />

      {userDoc && (
        <MobileEditGarageModal
          open={showMobileEditGarageModal}
          dismiss={() => setShowMobileEditGarageModal(false)}
          userDoc={userDoc}
          onAddBikeClick={handleAddBikeClick}
          onSyncSelectedBikes={handleSyncSelectedBikes}
          previousSelectedBikes={userDoc.bikes || []}
        />
      )}
    </>
  );
}

const BikeCard = ({
  bike,
  onBikeSelect,
  isSelected,
}: {
  bike: UserBike;
  onBikeSelect: (bike: UserBike | null) => void;
  isSelected: boolean;
}) => {
  const toggleSelectedBike = () => {
    if (!isSelected) {
      onBikeSelect(bike);
    } else {
      onBikeSelect(null);
    }
  };

  const parsedBrandName =
    bike.make === 'Gas Gas' // gas gas needs a special edge case to work
      ? bike.make.split(' ').join('').toLowerCase()
      : bike.make.split(' ').join('-').toLowerCase();

  const brandImage = `${prefix}/assets/brands/${parsedBrandName}_1920x2560.png`;

  return (
    <button
      type="button"
      className={cn(
        'flex h-full w-[34rem] flex-shrink-0 cursor-pointer items-center justify-between gap-x-[2rem] rounded-[2rem] bg-white px-[2rem] py-[0.5rem] shadow-md shadow-black/10',
        isSelected && 'border border-brand-secondary'
      )}
      onClick={toggleSelectedBike}
    >
      {brandImage ? (
        <SafeImage
          src={brandImage}
          alt={`${bike.make} ${bike.model}`}
          className="h-[7rem] w-[7rem] rounded-[0.5rem] object-contain"
          fallback={
            <div className="flex h-[7rem] w-[7rem] items-center justify-center rounded-[0.5rem] bg-zinc-200 p-1 text-center text-[1rem] font-medium text-zinc-600">
              {bike.make}
            </div>
          }
        />
      ) : (
        <div className="flex h-[7rem] w-[7rem] items-center justify-center rounded-[0.5rem] bg-zinc-200 p-1 text-[1rem] font-medium text-zinc-600">
          {bike.make}
        </div>
      )}

      <div className="flex grow flex-col items-start gap-y-2">
        <p className="text-[1.75rem] font-semibold text-black">{bike.make}</p>

        <div className="flex items-center gap-x-2">
          <p className="text-[1.5rem] font-medium text-[#444444]">
            {bike.year}
          </p>
          <p className="w-[11rem] truncate text-left text-[1.5rem] font-medium text-[#444444]">
            {bike.model}
          </p>
        </div>
      </div>

      <BikeCheckbox
        isSelected={isSelected}
        toggleSelectedBike={toggleSelectedBike}
      />
    </button>
  );
};

const BikeCheckbox = ({
  isSelected,
  toggleSelectedBike,
}: {
  isSelected: boolean;
  toggleSelectedBike: () => void;
}) => {
  return !isSelected ? (
    <MotionDiv
      className="h-[2.5rem] w-[2.5rem] rounded-full border border-zinc-300"
      onClick={(e) => {
        e.stopPropagation();
        toggleSelectedBike();
      }}
      initial={{ scale: 0.9, opacity: 0.5 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.2 }}
    />
  ) : (
    <MotionDiv
      className="flex h-[2.5rem] w-[2.5rem] items-center justify-center rounded-full bg-brand-secondary"
      onClick={(e) => {
        e.stopPropagation();
        toggleSelectedBike();
      }}
      initial={{ scale: 0.9, opacity: 0.5 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.2 }}
    >
      <CheckmarkIcon variant="white" height={10} width={10} />
    </MotionDiv>
  );
};
